import React from "react";
import { Download, Smartphone, X } from "lucide-react";

import { InfoBanner } from "@/main/react/component/InfoBanner";
import { Localizer } from "@/main/react/Types";
import { isAndroidAppOfferable, playListingUrl } from "@/main/mobile-shell/androidAppOffer";
import { androidAppPromoState } from "@/main/react/page/home-page/androidAppPromoState";
import { trackAppStoreLinkClicked } from "@/main/analytics/analyticsEvents";

/**
 * "Seavien is now on Google Play", on the home feed of an Android phone browser (2026-09-11).
 *
 * <p>Design: {@code designs/01-now/2026-09-11-android-app-install-promotion-design.md} §3.3. The sidebar
 * already carries the same offer, but behind a menu most members never open; this strip is the one place
 * the offer is made without being asked for, so it is held to three things:
 * <ul>
 *   <li><b>Only where the store can deliver</b> — {@code isAndroidAppOfferable()}: an Android browser,
 *   not the native shell, not an in-app webview, and a UI language the listing's markets speak.</li>
 *   <li><b>Quiet</b> (tenet T4): the neutral {@code InfoBanner}, one sentence, one action, one close.</li>
 *   <li><b>Every session, easy to dismiss</b> (operator decision 2026-09-23): views and taps never retire it;
 *   a dismiss hides it for the rest of the session, at most {@code DISMISS_MAX_HOURS} ({@code androidAppPromoState}).</li>
 * </ul>
 *
 * <p>Eligibility is decided ONCE at mount.
 */
export function AndroidAppPromoStrip({ localizer }: { localizer: Localizer }) {
    const [visible, setVisible] = React.useState<boolean>(
        () => isAndroidAppOfferable() && !androidAppPromoState.isSnoozed(),
    );

    if (!visible) {
        return null;
    }

    const dismiss = () => {
        androidAppPromoState.dismiss();
        setVisible(false);
    };

    const handleOpen = () => {
        // A tap hides the strip for this page view only; nothing is stored, so a member who came back
        // from the store without installing is offered it again on their next visit.
        trackAppStoreLinkClicked("home_banner");
        // Hide AFTER the click's native navigation has been allowed to commit, never synchronously
        // inside it: unmounting the anchor during the click would cancel the navigation (the same trap
        // MenuSideBarView's closeSidebarAfterNavigation documents).
        window.setTimeout(() => setVisible(false), 0);
    };

    return (
        <div className="mb-4" data-playwright-id="app-promo-play-strip">
            <InfoBanner
                icon={Smartphone}
                description={
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <span className="min-w-0">{localizer("app_promo.play.message")}</span>
                        <div className="flex shrink-0 items-center gap-2">
                            <a
                                href={playListingUrl("home_banner")}
                                onClick={handleOpen}
                                className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-text-main standard-hover focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                                data-playwright-id="app-promo-play-open"
                            >
                                <Download className="size-3.5" aria-hidden="true" />
                                {localizer("app_promo.play.action")}
                            </a>
                            <button
                                type="button"
                                onClick={dismiss}
                                aria-label={localizer("onboarding.aria.dismiss")}
                                className="relative touch-hit-44 inline-flex items-center justify-center rounded-full border border-border p-1.5 text-text-main standard-hover focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                                data-playwright-id="app-promo-play-dismiss"
                            >
                                <X className="size-3.5" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                }
            />
        </div>
    );
}

export default AndroidAppPromoStrip;
